import { Box, DialogContent, DialogTitle, Typography } from "@mui/material";
import { productBussiness } from "api/bussiness/product/product-bussiness";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import { observer } from "mobx-react-lite";
import { IUpdateManufactoring } from "mobx-stores/_models/products/manufactoring";
import { useStores } from "mobx-stores/_root-store";
import { useCallback } from "react";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { getManufactoringFormSchema } from "./schema";
import { IControl } from "../../../commons/ultilities/dynamic-form/models/control";
import { blue } from "@mui/material/colors";

const FIELD_GROUPS = {
  "Xuất giấy": new Set([
    "paperOutputStandard", "firstFullPaperSize", "firstFullPaperLength",
    "firstFullPaperPrintQuantity", "secondFullPaperSize", "secondFullPaperLength",
    "secondFullPaperPrintQuantity", "printPaperSize", "printPaperLength",
    "printPaperSP", "secondPrintPaperSize", "secondPrintPaperLength", "secondPrintPaperSP"
  ]),
  "In ấn": new Set([
    "printStandard", "frontPrintColor", "frontPrintColorCode",
    "frontPrintMatte", "frontPrintHandleWay", "frontPrintOtherHandling",
    "backPrintColor", "backPrintColorCode", "backPrintMatte",
    "backPrintHandleWay", "backPrintOtherHandling", "printNote"
  ]),
  "Ép nhũ": new Set(["foil", "foilProcessingStandard", "foilNote"]),
  "Bế định hình": new Set(["shape", "shapeFormingStandard", "shapeNote"]),
  "Cắt T/P": new Set(["cut", "cutStandard", "cutNote"]),
  "Dán T/P": new Set(["paste", "pasteStandard", "pasteNote"]),
  "Đóng gói": new Set(["package", "packageStandard", "packageNote"]),
};
const UNGROUPED_FIELDS_1 = new Set(["coatingStandard", "acceptanceStandard", "otherHandle"]);
const UNGROUPED_FIELDS_2 = new Set(["standard", "handleDetail", "note"]);

export const UpdateManufactoring = observer(() => {
  const {
    productManagement: {
      openUpdateManufactoring,
      setProp,
      productDetail,
      getProductDetail,
    },
  } = useStores();
  const {
    form: { controls, isValid },
    submitHandler,
  } = useForm<IUpdateManufactoring>(getManufactoringFormSchema(), {
    backPrintColor: productDetail?.process?.backPrintColor,
    backPrintColorCode: productDetail?.process?.backPrintColorCode || "",
    backPrintMatte: productDetail?.process?.backPrintMatte || "",
    cut: productDetail?.process?.cut || "",
    foil: productDetail?.process?.foil || "",
    frontPrintColor: productDetail?.process?.frontPrintColor,
    frontPrintColorCode: productDetail?.process?.frontPrintColorCode || "",
    frontPrintMatte: productDetail?.process?.frontPrintMatte || "",
    note: productDetail?.process?.note || "",
    otherHandle: productDetail?.process?.otherHandle || "",
    package: productDetail?.process?.package || "",
    paste: productDetail?.process?.paste || "",
    printPaperLength: productDetail?.process?.printPaperLength,
    printPaperSize: productDetail?.process?.printPaperSize,
    printPaperSP: productDetail?.process?.printPaperSP,
    shape: productDetail?.process?.shape || "",
    standard: productDetail?.process?.standard || "",
    printStandard: productDetail?.process?.printStandard || "",
    handleDetail: productDetail?.process?.handleDetail || "",
    backPrintHandleWay: productDetail?.process?.backPrintHandleWay,
    frontPrintHandleWay: productDetail?.process?.frontPrintHandleWay,
    secondFullPaperLength: productDetail?.process?.secondFullPaperLength,
    secondFullPaperSize: productDetail?.process?.secondFullPaperSize,
    firstFullPaperLength: productDetail?.process?.firstFullPaperLength,
    firstFullPaperSize: productDetail?.process?.firstFullPaperSize,
    firstFullPaperPrintQuantity:
      productDetail?.process?.firstFullPaperPrintQuantity,
    secondFullPaperPrintQuantity:
      productDetail?.process?.secondFullPaperPrintQuantity,
    secondPrintPaperLength: productDetail?.process?.secondPrintPaperLength,
    secondPrintPaperSize: productDetail?.process?.secondPrintPaperSize,
    secondPrintPaperSP: productDetail?.process?.secondPrintPaperSP,
    paperOutputStandard: productDetail?.process?.paperOutputStandard || "",
    frontPrintOtherHandling:
      productDetail?.process?.frontPrintOtherHandling || "",
    backPrintOtherHandling: productDetail?.process?.backPrintOtherHandling || "",
    printNote: productDetail?.process?.printNote || "",
    coatingStandard: productDetail?.process?.coatingStandard || "",
    acceptanceStandard: productDetail?.process?.acceptanceStandard || "",
    foilProcessingStandard: productDetail?.process?.foilProcessingStandard || "",
    foilNote: productDetail?.process?.foilNote || "",
    shapeFormingStandard: productDetail?.process?.shapeFormingStandard || "",
    shapeNote: productDetail?.process?.shapeNote || "",
    cutStandard: productDetail?.process?.cutStandard || "",
    cutNote: productDetail?.process?.cutNote || "",
    pasteStandard: productDetail?.process?.pasteStandard || "",
    pasteNote: productDetail?.process?.pasteNote || "",
    packageStandard: productDetail?.process?.packageStandard || "",
    packageNote: productDetail?.process?.packageNote || "",
  });
  const filterControls = (controls: IControl[], fieldNames: Set<string>) => controls.filter(c => fieldNames.has(c.name));
  const saveHandler = useCallback(
    async (data: IUpdateManufactoring) => {
      if (!productDetail || !productDetail?.id) return;
      await productBussiness.updateManufactoring(productDetail?.id, data);
      await getProductDetail(productDetail?.id);
      setProp("openUpdateManufactoring", false);
    },
    [getProductDetail, productDetail, setProp]
  );

  return (
    <DialogForm
      open={openUpdateManufactoring}
      fullWidth
      maxWidth={"md"}
      onClose={() => setProp("openUpdateManufactoring", false)}
      onCancelBtnClicked={() => setProp("openUpdateManufactoring", false)}
      onSaveBtnClick={async () => {
        await submitHandler(saveHandler);
      }}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>
        {`Cập nhật sản xuất: `}
        <span style={{ color: "blue" }}>{`${productDetail?.name}${
          productDetail?.customer?.dealingName
            ? `- ${productDetail?.customer?.dealingName}`
            : ""
        }`}</span>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: 4 }}>
          <FieldsGroup title="Xuất giấy" controls={filterControls(controls, FIELD_GROUPS['Xuất giấy'])} />
          <FieldsGroup title="In ấn" controls={filterControls(controls, FIELD_GROUPS['In ấn'])} />
          <DynamicForm controls={filterControls(controls, UNGROUPED_FIELDS_1)} />
          <FieldsGroup title="Ép nhũ" controls={filterControls(controls, FIELD_GROUPS['Ép nhũ'])} />
          <FieldsGroup title="Bế định hình" controls={filterControls(controls, FIELD_GROUPS['Bế định hình'])} />
          <FieldsGroup title="Cắt T/P" controls={filterControls(controls, FIELD_GROUPS['Cắt T/P'])} />
          <FieldsGroup title="Dán T/P" controls={filterControls(controls, FIELD_GROUPS['Dán T/P'])} />
          <FieldsGroup title="Đóng gói" controls={filterControls(controls, FIELD_GROUPS['Đóng gói'])} />
          <DynamicForm controls={filterControls(controls, UNGROUPED_FIELDS_2)} />
        </Box>
      </DialogContent>
    </DialogForm>
  );
});

function FieldsGroup({ title, controls }: { title: string; controls: IControl[] }) {
  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2, pt: 4, position: "relative" }}>
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        sx={{
          position: "absolute",
          top: -12,
          left: 8,
          bgcolor: "background.paper",
          color: "primary.main",
          px: 1,
        }}
      >
        {title}
      </Typography>
      <DynamicForm controls={controls} />
    </Box>
  );
}
